import React from 'react';
import { TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';

import { QuoteData, Order } from './data';
import quoteHeadCells from './headCells';

/**
 * Props for the `QuoteTableHead` component.
 */
interface EnhancedTableProps {
	onRequestSort: (event: React.MouseEvent<unknown>, property: keyof QuoteData) => void;
	order: Order;
	orderBy: string;
	rowCount: number;
}

/**
 * Component representing the table header for the quotes table.
 *
 * @param {EnhancedTableProps} props - The props for the component.
 * @returns {JSX.Element} - The rendered component.
 */
export function QuoteTableHead(props: EnhancedTableProps) {
	const { order, orderBy, rowCount, onRequestSort } = props;
	const createSortHandler = (property: keyof QuoteData) => (event: React.MouseEvent<unknown>) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead
			sx={{
				backgroundColor: '#f6f6f6',
				'& .MuiTableRow-root': {
					'& .MuiTableCell-root': {
						fontWeight: 700,
						fontSize: '14px',
						color: '#515151',
						letterSpacing: 0,
					},
				},
			}}
		>
			<TableRow>
				{quoteHeadCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={
							headCell.numeric ? 'right' : headCell.id === 'id' ? 'center' : 'left'
						}
						padding='normal'
						sortDirection={orderBy === headCell.id ? order : false}
						{...(headCell.colSpan && { colSpan: headCell.colSpan })}
					>
						{headCell.sortable !== false ? (
							<TableSortLabel
								active={orderBy === headCell.id}
								direction={orderBy === headCell.id ? order : 'asc'}
								onClick={createSortHandler(headCell.id)}
							>
								{headCell.label}
								{orderBy === headCell.id ? (
									<span hidden={true}>
										{order === 'desc'
											? 'sorted descending'
											: 'sorted ascending'}
									</span>
								) : null}
							</TableSortLabel>
						) : headCell.component ? (
							<>{headCell.component}</>
						) : (
							<span>{headCell.label}</span>
						)}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}
